import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { format } from "date-fns";
import { motion } from "framer-motion";
import Confetti from "react-confetti";

export default function BookingConfirmation() {
    const { token } = useParams();
    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const [showConfetti, setShowConfetti] = useState(false);

    // Track window size for confetti
    useEffect(() => {
        const updateSize = () => {
            setDimensions({ width: window.innerWidth, height: window.innerHeight });
        };
        updateSize();
        window.addEventListener("resize", updateSize);
        return () => window.removeEventListener("resize", updateSize);
    }, []);

    // Fetch booking data
    useEffect(() => {
        const fetchBooking = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/bookings/token/${token}`);
                if (!res.ok) throw new Error("Booking not found");
                const data = await res.json();
                setBooking(data);

                // Show confetti if status is confirmed
                if (data.status && data.status.toLowerCase() === "confirmed") {
                    setShowConfetti(true);
                    setTimeout(() => setShowConfetti(false), 5000);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchBooking();
    }, [token]);

    if (loading)
        return (
            <div className="flex items-center justify-center min-h-screen">
                <p className="text-[#144E73] text-lg">Loading booking details...</p>
            </div>
        );

    if (error)
        return (
            <div className="flex items-center justify-center min-h-screen">
                <p className="text-red-500 text-lg">{error}</p>
            </div>
        );

    const startDate = booking.start_date ? format(new Date(booking.start_date), "MMM dd, yyyy") : "";
    const endDate = booking.end_date ? format(new Date(booking.end_date), "MMM dd, yyyy") : "";

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F2F1F0] px-4 relative">
            {showConfetti && <Confetti width={dimensions.width} height={dimensions.height} numberOfPieces={200} />}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white rounded-xl shadow-md p-8 max-w-lg w-full border border-[#1D4E80] text-[#144E73]"
            >
                <h1 className="text-3xl md:text-4xl font-bold mb-6 text-center">Booking Details</h1>

                <div className="space-y-3 text-[#023059]">
                    <p><span className="font-semibold">Name:</span> {booking.full_name}</p>
                    <p><span className="font-semibold">Hotel:</span> {booking.hotel}</p>
                    <p><span className="font-semibold">Dates:</span> {startDate} – {endDate}</p>
                    <p><span className="font-semibold">Guests:</span> {booking.guests}</p>
                    {booking.company && <p><span className="font-semibold">Company:</span> {booking.company}</p>}
                    {booking.notes && <p><span className="font-semibold">Notes:</span> {booking.notes}</p>}
                    <p>
                        <span className="font-semibold">Status:</span>{" "}
                        {booking.status ? booking.status.charAt(0).toUpperCase() + booking.status.slice(1) : "Pending"}
                        {booking.status?.toLowerCase() !== "confirmed" && " (Not yet confirmed)"}
                    </p>
                </div>

                <div className="mt-6 text-center">
                    <a
                        href="/"
                        className="inline-block px-6 py-3 bg-[#F29E38] text-[#023059] font-semibold rounded-full hover:bg-opacity-90 transition"
                    >
                        Return Home
                    </a>
                </div>
            </motion.div>
        </div>
    );
}
